import { buttonVariants } from '@/components/shadcn/button'
import { IMG_BASE_PATH } from '@/const'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { Loading } from '@/ui-legacy/Loading'
import { Edit24Regular } from '@fluentui/react-icons'
import './AppMarketingPanel.scss'
import { ApplicationsBreadcrumb } from '@/pages/Applications/ApplicationsBreadcrumbs'
import { Link } from 'react-router'
import { useCurrentApplication } from '../useCurrentApplication'

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesApplicationsApplicationAttributes = RestSchemas['Messages.Applications.ApplicationAttributes']
type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

export const AppDetailsPreview = () => {
  const application = useCurrentApplication()

  const { getApplicationsApiGetApplicationAttributes } = useRestClient()

  const {
    data: applicationAtt,
    isPending: isApplicationAttPending,
    isError: isApplicationAttError,
  } = getApplicationsApiGetApplicationAttributes.useQuery({
    query: { applicationId: application.applicationId },
  })

  if (isApplicationAttPending) return <Loading />
  if (isApplicationAttError || !application) return <ErrorComponent />
  return <PageContent {...{ application, applicationAtt }} />
}

function PageContent({
  application,
  applicationAtt,
}: {
  application: MessagesApplicationsApplication
  applicationAtt: MessagesApplicationsApplicationAttributes
}) {
  // const [editAppAttrOpen, setEditAppAttrOpen] = useState(false)
  const imagePrefix = `${IMG_BASE_PATH}`

  const { appName } = application

  const {
    differentiatedBenefit1Pictogram,
    differentiatedBenefit1,
    differentiatedBenefit2Pictogram,
    differentiatedBenefit2,
    differentiatedBenefit3Pictogram,
    differentiatedBenefit3,
    imageKey,
    problemStatement,
    keyQuestions,
    solutionDescription,
    keyContactId1,
    keyContactId2,
    keyContactId3,
  } = applicationAtt

  const { getSubscriptionsApiGetOrganisationUser } = useRestClient()

  // Queries
  const { data: keyContact1 } = getSubscriptionsApiGetOrganisationUser.useQuery(
    {
      query: { userId: keyContactId1 ?? undefined },
    },
    { enabled: !!keyContactId1 }
  )

  const { data: keyContact2 } = getSubscriptionsApiGetOrganisationUser.useQuery(
    {
      query: { userId: keyContactId2 ?? undefined },
    },
    { enabled: !!keyContactId2 }
  )

  const { data: keyContact3 } = getSubscriptionsApiGetOrganisationUser.useQuery(
    {
      query: { userId: keyContactId3 ?? undefined },
    },
    { enabled: !!keyContactId3 }
  )

  // Data Arrays to render
  const keyContacts = [keyContact1, keyContact2, keyContact3].filter(Boolean)

  const differentiatedBenefits = [
    { pictogram: differentiatedBenefit1Pictogram, text: differentiatedBenefit1 },
    { pictogram: differentiatedBenefit2Pictogram, text: differentiatedBenefit2 },
    { pictogram: differentiatedBenefit3Pictogram, text: differentiatedBenefit3 },
  ].filter(({ text, pictogram }) => !!text || !!pictogram)

  return (
    <>
      <ApplicationsBreadcrumb title="Marketing" />
      <div className="pt-5">
        <div className="application-card">
          <div className="application-content">
            <div className="header-layout">
              <div className="section-heading">{appName}</div>

              {imageKey && (
                <div
                  className="key-image"
                  style={{
                    backgroundImage: `url("${imagePrefix}${imageKey}")`,
                  }}
                />
              )}
            </div>

            <div className="section-heading">{problemStatement}</div>
            <div className="section-text">{keyQuestions}</div>
            <div className="section-heading">How this app tackles the problem statement</div>
            <hr />
            <div className="section-text">{solutionDescription}</div>

            <br />
            <div className="section-heading">Benefits that differentiate this app</div>
            <hr />
            <div className="benefits">
              {differentiatedBenefits.map(({ text, pictogram }) => (
                <div key={`${text}-${pictogram}`} className="benefit">
                  {pictogram && (
                    <div
                      className="benefit-image"
                      style={{
                        backgroundImage: `url("${imagePrefix}${pictogram}")`,
                      }}
                    />
                  )}
                  <div className="benefit-description">{text}</div>
                </div>
              ))}
            </div>
            <br />
            <div className="section-heading">Get in touch with our app leads</div>
            <hr />
            <div className="keyContacts">
              {keyContacts.map((keyContact) => (
                <KeyContact key={keyContact?.id} keyContact={keyContact!} />
              ))}
            </div>
          </div>
          <div>
            <Link to="edit-attributes" className={buttonVariants({ variant: 'outline', size: 'icon' })}>
              <Edit24Regular />
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}

function KeyContact({ keyContact }: { keyContact: MessagesSubscriptionsOrganisationUser }) {
  const imagePrefix = `${IMG_BASE_PATH}`
  return (
    <div className="keyContact">
      {keyContact.profilePicture && (
        <div
          className="keyContact-image"
          style={{ backgroundImage: `url("${imagePrefix}${keyContact.profilePicture}")` }}
        />
      )}
      <div className="keyContactName">
        {keyContact.givenName}
        &nbsp;
        {keyContact.familyName}
      </div>
      <div className="keyContactEmail">{keyContact.email}</div>
    </div>
  )
}
